import { Box, IconButton, Tooltip, Button } from '@mui/material'
import { Icon as Iconify } from '@iconify/react'
import { useMemo, useState, useEffect, useCallback, useRef } from 'react'
import { useOverflowState, buildMask } from 'components/common/hooks/useOverflowState'
import useInnovaTheme from 'components/common/hooks/useInnovaTheme'
import { keyOf } from './pinFilterUtils'
import { debounce } from 'lodash'
import { appColors } from 'utils'

const CHEVRON_W = 18

export default function PinnedFilterCarousel({
  pinnedItemOptions,
  pinnedGridFilters,
  activePinned,
  initialExternalPinnedActive,
  pinned,
  onChangeActivePinnedFilters,
  maxWidth = '35vw',

  // disable filter functionality
  gridApi, // disable based on current grid state
  gridReady, // must be included with gridApi
  allowedChips, // alternative to using gridApi to disable filters. will disable any filter not in this list of {column, value} objects
}) {
  const { ref, hasOverflow, atStart, atEnd } = useOverflowState([pinnedGridFilters])
  const { getDisabledColor, getTextColor } = useInnovaTheme()
  const [hover, setHover] = useState(false)
  const [disabledMap, setDisabledMap] = useState({})

  const displayedFilters = useMemo(() => {
    if (!Array.isArray(pinnedGridFilters)) return []

    return pinnedGridFilters.filter((filter) =>
      pinnedItemOptions[filter.column]?.values?.some((value) => value === filter.value),
    )
  }, [pinnedGridFilters, pinnedItemOptions])

  const checkDisplayedFiltersValidWithGrid = useCallback(() => {
    const api = gridApi?.current
    if (!api) return

    if (!displayedFilters?.length) {
      setDisabledMap({})
      return
    }

    const activeCols = new Set(Object.keys(api.getFilterModel() || {}))

    const need = new Map()
    displayedFilters.forEach((chip) => {
      if (chip.external) return
      if (activeCols.has(chip.column)) return

      const col = chip.column
      const val = String(chip.value).trim()

      if (!need.has(col)) need.set(col, new Set())
      need.get(col).add(val)
    })

    if (need.size === 0) {
      setDisabledMap({})
      return
    }

    let remaining = 0
    need.forEach((set) => (remaining += set.size))

    api.forEachNodeAfterFilter?.((node) => {
      if (remaining === 0) return
      if (node.group || !node.data) return 

      for (const [col, want] of need) {
        if (!want.size) continue
        const raw = node.data[col]
        if (raw === null || raw === '') continue
        const norm = String(raw).trim()
        if (want.delete(norm)) {
          remaining--
          if (want.size === 0) need.delete(col)
        }
      }
    })

    const next = {}
    displayedFilters.forEach((chip) => {
      if (chip.external) return
      if (activeCols.has(chip.column)) return

      const missing = need.get(chip.column)?.has(String(chip.value).trim()) ?? false
      next[keyOf(chip.column, chip.value)] = missing
    })

    setDisabledMap(next)
  }, [gridApi, displayedFilters])

  const stableCheckDisplayedFiltersGridValid = useRef(checkDisplayedFiltersValidWithGrid)

  useEffect(() => {
    stableCheckDisplayedFiltersGridValid.current = checkDisplayedFiltersValidWithGrid
  }, [checkDisplayedFiltersValidWithGrid])

  const debounceCheckDisplayedFiltersGridValid = useMemo(() => {
    const fn = () => stableCheckDisplayedFiltersGridValid.current?.()
    return debounce(fn, 200, { trailing: true, leading: false })
  }, [])

  useEffect(() => {
    if (!gridReady) return

    const api = gridApi?.current
    if (!api) return

    api.addEventListener?.('filterChanged', debounceCheckDisplayedFiltersGridValid)
    api.addEventListener?.('modelUpdated', debounceCheckDisplayedFiltersGridValid)
    api.addEventListener?.('rowDataUpdated', debounceCheckDisplayedFiltersGridValid)

    debounceCheckDisplayedFiltersGridValid()
    return () => {
      api.removeEventListener?.('filterChanged', debounceCheckDisplayedFiltersGridValid)
      api.removeEventListener?.('modelUpdated', debounceCheckDisplayedFiltersGridValid)
      api.removeEventListener?.('rowDataUpdated', debounceCheckDisplayedFiltersGridValid)

      debounceCheckDisplayedFiltersGridValid.cancel()
    }
  }, [gridApi, debounceCheckDisplayedFiltersGridValid, gridReady])

  useEffect(() => {
    if (gridApi?.current) return
    const activeCols = new Set(Object.keys(activePinned || {}).map((k) => k.split('::')[0]))
    if (!allowedChips) {
      setDisabledMap({})
      return
    }

    const next = {}
    if (!Array.isArray(displayedFilters)) return
    displayedFilters.forEach(({ column, value }) => {
      if (activeCols.has(column)) return

      const allowed = allowedChips[column]?.values ?? []
      if (!allowed.includes(value)) next[keyOf(column, value)] = true
    })

    setDisabledMap(next)
  }, [allowedChips, displayedFilters, gridApi, activePinned])

  useEffect(() => {
    if (!initialExternalPinnedActive) return

    const updates = {}
    Object.keys(initialExternalPinnedActive).forEach((key) => {
      if (initialExternalPinnedActive[key] && !activePinned[key]) {
        updates[key] = true
      }
    })

    if (Object.keys(updates).length) {
      pinned?.update?.(updates)
    }
  }, [initialExternalPinnedActive, pinned, activePinned])

  const mask = useMemo(() => {
    return buildMask(!atStart, !atEnd)
  }, [atStart, atEnd])

  const scrollByAmount = (dir) => {
    const el = ref.current
    if (!el) return
    const amount = Math.max(180, Math.floor(el.clientWidth * 0.8))
    el.scrollBy({ left: dir * amount, behavior: 'smooth' })
  }

  const handleSetActivePinnedFilters = (filter) => {
    pinned?.toggle?.(filter)
    if (onChangeActivePinnedFilters) onChangeActivePinnedFilters(filter)
  }

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        height: '100%',
        width: '100%',
        maxWidth: maxWidth,
        alignItems: 'center',
        justifyContent: 'start',
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}>
      {hasOverflow && !atStart && (
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 2,
            width: CHEVRON_W,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'auto',
          }}>
          <IconButton
            size='small'
            onClick={() => scrollByAmount(-1)}
            disabled={!hasOverflow || atStart}
            sx={{
              opacity: hasOverflow ? (hover ? 1 : 0.6) : 0,
              visibility: hasOverflow ? 'visible' : 'hidden',
              transition: 'opacity 120ms',
              bgcolor: 'transparent',
              '&:hover': { bgcolor: 'transparent' },
            }}>
            <Iconify icon='lucide:chevron-left' />
          </IconButton>
        </Box>
      )}

      <Box
        ref={ref}
        className='scrollbar-local'
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'start',
          height: '100%',
          width: '100%',
          overflowX: 'auto',
          scrollbarWidth: 'none',
          WebkitMaskImage: mask,
          maskImage: mask,
          gap: 1,
        }}>
        {displayedFilters.map((filter) => {
          const isActive = activePinned?.[`${filter.column}::${filter.value}`]
          const isDisabled = disabledMap?.[`${filter.column}::${filter.value}`]
          return (
            <Box key={`${filter.column}::${filter.value}`}>
              <Tooltip title={`${isActive ? 'Remove' : 'Apply'} filter for ${filter.columnLabel}`} arrow>
                <span>
                  <Button
                    variant='outlined'
                    size='small'
                    onClick={() => handleSetActivePinnedFilters(filter)}
                    disabled={isDisabled}
                    sx={{
                      whiteSpace: 'nowrap',
                      minWidth: 0,
                      height: 25,
                      m: 0,
                      px: 1,
                      borderRadius: 1.5,
                      color: getTextColor(),
                      borderColor: isActive ? appColors.itemTextColor : appColors.borderColorLight,
                      lineHeight: 1.2,
                      textTransform: 'none',
                      '&.Mui-disabled': {
                        borderColor: getDisabledColor(),
                        color: getDisabledColor(),
                      },
                    }}>
                    {filter.value}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          )
        })}
      </Box>

      <Box
        sx={{
          position: 'absolute',
          right: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 2,
          width: CHEVRON_W,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: hasOverflow ? 'auto' : 'none',
        }}>
        <IconButton
          size='small'
          onClick={() => scrollByAmount(1)}
          disabled={!hasOverflow || atEnd}
          sx={{
            opacity: hasOverflow ? (hover ? 1 : 0.6) : 0,
            visibility: hasOverflow ? 'visible' : 'hidden',
            transition: 'opacity 120ms',
            bgcolor: 'transparent',
            '&:hover': { bgcolor: 'transparent' },
          }}>
          <Iconify icon='lucide:chevron-right' />
        </IconButton>
      </Box>
    </Box>
  )
}
